import { useEffect, useState } from 'react';

import { MapGeoJSONFeature, Map as MapLibreMap, MapMouseEvent } from 'maplibre-gl';

import { PC_LAYER, PC_POLY_FILL, REGION_FILL, REGION_LABEL } from '../constants';
import { PostcodeMarker, TooltipState } from '../types';

interface UseMapInteractionsOptions {
  mapRef: React.MutableRefObject<MapLibreMap | null>;
  ready: boolean;
  geojson: GeoJSON.FeatureCollection | null;
  activeRegion: string | null;
  setActiveRegion: (name: string | null) => void;
  allMarkers: PostcodeMarker[];
  regionNameProperty: string;
  onPostcodeClick: (pc: string) => void;
}

/**
 * Wires up all mouse interactions on the map:
 *   - Region hover (feature-state for color overlay)
 *   - Region label hover (shows simple name tooltip)
 *   - Region click  — fitBounds + setActiveRegion
 *   - Postcode dot hover  — tooltip with full info
 *   - Postcode polygon hover — same tooltip
 *   - Postcode click — sets focused postcode (drives focus border)
 *
 * Returns tooltip state which the parent renders via the appropriate
 * tooltip card component.
 */
export function useMapInteractions({
  mapRef,
  ready,
  geojson,
  activeRegion,
  setActiveRegion,
  allMarkers,
  regionNameProperty,
  onPostcodeClick,
}: UseMapInteractionsOptions) {
  const [tooltip, setTooltip] = useState<TooltipState>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !geojson) return;

    let hoveredRegionId: number | string | undefined;

    const clearRegionHover = () => {
      // The effect's cleanup runs on unmount, by which point the map may have
      // been removed (useMapLibre destroys it). Bail if the instance is gone
      // or its style/canvas are no longer available.
      if (!mapRef.current || !map.getCanvas || !map.style) return;
      try {
        if (hoveredRegionId !== undefined) {
          map.setFeatureState({ source: 'regions', id: hoveredRegionId }, { hover: false });
          hoveredRegionId = undefined;
        }
        map.getCanvas().style.cursor = '';
      } catch {
        // Map was torn down mid-cleanup — nothing to reset.
      }
    };

    // ── Region hover ─────────────────────────────────────────────────────────
    const onRegionMove = (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
      if (activeRegion) return; // hover disabled while a region is open
      if (!e.features?.length) return;
      const f = e.features[0];

      if (hoveredRegionId !== undefined) {
        map.setFeatureState({ source: 'regions', id: hoveredRegionId }, { hover: false });
      }
      hoveredRegionId = f.id;
      if (hoveredRegionId !== undefined) {
        map.setFeatureState({ source: 'regions', id: hoveredRegionId }, { hover: true });
      }
      map.getCanvas().style.cursor = 'pointer';

      const name = String(f.properties?.[regionNameProperty] ?? '');
      setTooltip({ kind: 'region', name, x: e.originalEvent.clientX, y: e.originalEvent.clientY });
    };

    const onRegionLeave = () => {
      clearRegionHover();
      setTooltip(null);
    };

    // ── Region click — drop hover so the region isn't stuck highlighted ──────
    const onRegionClick = (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
      if (!e.features?.length) return;
      const f = e.features[0];
      const name = String(f.properties?.[regionNameProperty] ?? '');
      if (!name) return;
      // Reset visual hover state immediately — mouseleave often doesn't fire
      // because the camera flyTo moves the map out from under the cursor.
      clearRegionHover();
      // Zoom animation lives in CountryView/index.tsx as a single effect on
      // activeRegion — so the same transition runs whether the user clicked
      // the map or chose from the sidebar.
      setActiveRegion(name);
      setTooltip(null);
    };

    // ── Postcode hover — shared logic for tooltip & cursor ───────────────────
    // Works for two sources:
    //   - dot circles (PC_LAYER)         — count always > 0, place from marker
    //   - polygon fills (PC_POLY_FILL)   — count may be 0, props from feature
    const showPcTooltip = (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
      if (!e.features?.length) return null;
      const f = e.features[0];
      const pc = String(f.properties?.postcode ?? '');
      if (!pc) return null;

      // Try the live incident marker first
      let marker = allMarkers.find((m) => m.postcode === pc);

      // Fall back to a synthetic empty marker so we can hover/tooltip on
      // postcode zones without incidents (showing "0 incidents").
      if (!marker) {
        marker = {
          postcode: pc,
          place: String(f.properties?.place ?? f.properties?.Gmina ?? ''),
          count: 0,
          region: String(f.properties?.region ?? ''),
          lng: 0,
          lat: 0,
        };
      }

      map.getCanvas().style.cursor = 'pointer';
      setTooltip({
        kind: 'postcode',
        marker,
        x: e.originalEvent.clientX,
        y: e.originalEvent.clientY,
      });
      return f;
    };

    // Dots — tooltip only (no feature-state hover layer for circles)
    const onDotMove = (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
      showPcTooltip(e);
    };

    // Polygons — tooltip + feature-state hover for the highlight overlay
    let hoveredPolyId: number | string | undefined;
    const clearPolyHover = () => {
      if (!mapRef.current || !map.style) return;
      try {
        if (hoveredPolyId !== undefined) {
          map.setFeatureState({ source: 'postcode-polygons', id: hoveredPolyId }, { hover: false });
          hoveredPolyId = undefined;
        }
      } catch {
        // Map torn down — ignore.
      }
    };
    const onPolyMove = (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
      const f = showPcTooltip(e);
      if (!f) return;
      if (hoveredPolyId !== undefined && hoveredPolyId !== f.id) {
        map.setFeatureState({ source: 'postcode-polygons', id: hoveredPolyId }, { hover: false });
      }
      hoveredPolyId = f.id;
      if (hoveredPolyId !== undefined) {
        map.setFeatureState({ source: 'postcode-polygons', id: hoveredPolyId }, { hover: true });
      }
    };

    const onPcLeave = () => {
      clearPolyHover();
      map.getCanvas().style.cursor = '';
      setTooltip(null);
    };

    // ── Postcode click — focus only (zoom logic lives in parent). Empty
    //    zones (count === 0) are ignored — no point focusing a zone with no data.
    const onPcClick = (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
      if (!e.features?.length) return;
      const f = e.features[0];
      const pc = String(f.properties?.postcode ?? '');
      const count = Number(f.properties?.count ?? 0);
      if (!pc || count <= 0) return;
      onPostcodeClick(pc);
    };

    // Wire up the events
    map.on('mousemove', REGION_FILL, onRegionMove);
    map.on('mouseleave', REGION_FILL, onRegionLeave);
    map.on('click', REGION_FILL, onRegionClick);

    map.on('mousemove', REGION_LABEL, onRegionMove);
    map.on('mouseleave', REGION_LABEL, onRegionLeave);
    map.on('click', REGION_LABEL, onRegionClick);

    // Dots — tooltip only
    map.on('mousemove', PC_LAYER, onDotMove);
    map.on('mouseleave', PC_LAYER, onPcLeave);
    map.on('click', PC_LAYER, onPcClick);

    // Polygons — tooltip + feature-state hover for the highlight overlay
    map.on('mousemove', PC_POLY_FILL, onPolyMove);
    map.on('mouseleave', PC_POLY_FILL, onPcLeave);
    map.on('click', PC_POLY_FILL, onPcClick);

    return () => {
      // Drop any lingering hover feature-state — when activeRegion changes,
      // this effect re-binds; without an explicit reset the hover color stays
      // on the region under the cursor at the time of the click.
      clearRegionHover();
      clearPolyHover();

      map.off('mousemove', REGION_FILL, onRegionMove);
      map.off('mouseleave', REGION_FILL, onRegionLeave);
      map.off('click', REGION_FILL, onRegionClick);
      map.off('mousemove', REGION_LABEL, onRegionMove);
      map.off('mouseleave', REGION_LABEL, onRegionLeave);
      map.off('click', REGION_LABEL, onRegionClick);
      map.off('mousemove', PC_LAYER, onDotMove);
      map.off('mouseleave', PC_LAYER, onPcLeave);
      map.off('click', PC_LAYER, onPcClick);
      map.off('mousemove', PC_POLY_FILL, onPolyMove);
      map.off('mouseleave', PC_POLY_FILL, onPcLeave);
      map.off('click', PC_POLY_FILL, onPcClick);
    };
  }, [
    mapRef,
    ready,
    geojson,
    activeRegion,
    setActiveRegion,
    allMarkers,
    regionNameProperty,
    onPostcodeClick,
  ]);

  return { tooltip };
}
